// In-place upsell for a 402.
//
// The client used to do `window.location.href = '/billing'`, which threw away
// everything the server told us — the 402 body already carries unit_id or level
// — and dropped the learner on a generic billing page with no memory of what
// they had been trying to open.
//
// Deliberately NOT a "return them to the unit after checkout" flow: Stripe
// activates the subscription via the checkout.session.completed webhook, so a
// redirect back to the unit can beat the webhook, 402 again, and loop.
import { track } from './analytics'

let host = null
// True when the modal was raised by a 402 on a page the learner cannot use.
// Dismissing then has to take them somewhere that works — leaving them on a
// half-loaded gated page is worse than the redirect this replaced.
let stranded = false

function leave() {
  if (window.history.length > 1) window.history.back()
  else window.location.href = '/'
}

function close() {
  if (host) { host.remove(); host = null }
  document.removeEventListener('keydown', onKey)
  if (stranded) { stranded = false; leave() }
}

function onKey(e) { if (e.key === 'Escape') close() }

export function showUpsell({ unitId = null, level = null, unitTitle = null, fromBlockedPage = false } = {}) {
  // isConnected, not just truthiness: if the node is torn out of the DOM by
  // anything other than close() — a re-render, a container being cleared — a
  // bare `if (host)` would latch permanently and silently swallow every later
  // 402 for the rest of the session.
  if (host?.isConnected) return
  if (host) close()
  stranded = fromBlockedPage
  track('paywall_hit', { unit_id: unitId, level: level })

  const what = unitTitle ? `“${unitTitle}”` : unitId ? 'This unit' : level ? `Level ${level}` : 'This'
  host = document.createElement('div')
  host.className = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4'
  host.innerHTML = `
    <div role="dialog" aria-modal="true" aria-label="Upgrade to Pro"
         class="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl p-6">
      <p class="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Speak Saigon Pro</p>
      <h2 class="mt-1 text-xl font-bold text-gray-900 dark:text-gray-50">${what} is part of Pro</h2>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
        Pro opens every unit, AI conversation practice, and unlimited custom tone practice.
        Everything you have learned so far stays yours either way.
      </p>
      <div class="mt-5 flex flex-col gap-2">
        <a href="/billing" data-upsell="upgrade"
           class="w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-lg transition-colors">
          See Pro
        </a>
        <button type="button" data-upsell="dismiss"
                class="w-full text-center text-gray-600 dark:text-gray-300 font-medium px-5 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          ${stranded ? 'Go back' : 'Keep exploring'}
        </button>
      </div>
    </div>`

  host.addEventListener('click', (e) => {
    if (e.target === host || e.target.closest('[data-upsell="dismiss"]')) {
      track('paywall_dismissed', { unit_id: unitId, level: level })
      close()
      return
    }
    if (e.target.closest('[data-upsell="upgrade"]')) {
      stranded = false // the link itself navigates; don't also go back
      track('paywall_upgrade_clicked', { unit_id: unitId, level: level })
    }
  })
  document.addEventListener('keydown', onKey)
  document.body.appendChild(host)
}
